import { useState } from 'react';
import { Select } from '@round/whitelabel-ui';
import { StylesConfig } from 'react-select';
import { creatorbase, PaypalTransactionStatus, tipalti } from '@round/api';
import { CellContext } from '@tanstack/react-table';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { showNotification } from 'helpers';
import styles from './PaymentStatusCell.module.css';
import { OPTIONS, SELECTABLE_OPTIONS } from './helpers';
import capitalize from 'lodash/capitalize';
import { GenericDropdownOption } from 'App.types';
import { getPaymentStatusLabel } from 'Modules/Plans/Finance/components/PaymentStatusBadge/helpers';
import PaymentStatusBadge from 'Modules/Plans/Finance/components/PaymentStatusBadge/PaymentStatusBadge';
import { getPrimaryPaymentStatus } from 'Modules/Plans/Finance/helpers';
import { usePostPaymentRequest } from '@round/creatorbase-helpers/api-hooks';

type Row = creatorbase.Post;

type Context<T extends Row> = CellContext<T, creatorbase.Post['payment_request_status']>;
const getTableMeta = getTableMetaHelper<BasePostTableMeta<Row>>();

// REQUESTED is a valid manual status, but it shouldn't be selectable by the user, so we're discluding it from state.
const getManualStatusValue = (value: creatorbase.PostPaymentStatus) => {
    return value === 'PAID' || value === 'UNPAID' ? value : null;
};
type ManualStatus = Extract<creatorbase.PostPaymentStatus, 'PAID' | 'UNPAID'>;

const formatOptionLabel = (
    option: GenericDropdownOption<
        | creatorbase.PostPaymentRequestStatus
        | PaypalTransactionStatus
        | creatorbase.PostPaymentStatus
        | tipalti.TipaltiTransactionStatus
    >
) => {
    return <PaymentStatusBadge status={option.value} className={styles.badge} />;
};

const PaymentStatusCell = <T extends Row>({ table, row, getValue }: Context<T>) => {
    const initialStatus = row.original.payment_status;
    const paymentRequestStatus = getValue();

    const isPaymentStatusEditable = !paymentRequestStatus || paymentRequestStatus === 'CANCELLED';

    const { data: latestPostPaymentRequest, isFetching: isLatestPostPaymentRequestFetching } = usePostPaymentRequest(
        row.original.payment_request_id,
        { enabled: !!paymentRequestStatus && paymentRequestStatus !== 'CANCELLED' }
    );

    const { isLoading, updatePost } = getTableMeta(table);

    const [manualStatusValue, setManualStatusValue] = useState<ManualStatus | null>(
        getManualStatusValue(initialStatus)
    );

    const handleUpdate = async (value: creatorbase.PostPaymentStatus) => {
        const errorMessage = 'Failed to update payment status';
        if (!updatePost) {
            return;
        }

        try {
            const response = await updatePost(row.original.id, {
                payment_status: value,
            });

            if (response.status === 200) {
                setManualStatusValue(getManualStatusValue(response.data.payment_status));
                showNotification('Payment status updated successfully', 'info');
                return;
            }

            setManualStatusValue(getManualStatusValue(initialStatus));
            showNotification(errorMessage, 'error');
        } catch {
            setManualStatusValue(getManualStatusValue(initialStatus));
            showNotification(errorMessage, 'error');
        }
    };

    const selectStyles: StylesConfig = {
        container: (base) => ({
            ...base,
            width: 'fit-content',
        }),
        control: (base) => ({
            ...base,
            width: 'max-content',
            padding: '0.25rem',
            transition: 'none',
            background: 'transparent',
            border: '1px solid transparent',
            ':hover': {
                padding: '0.25rem',
            },
        }),
        singleValue: (base) => ({
            ...base,
            position: 'static',
            top: '0',
            transform: 'none',
            fontSize: '0.75rem',
            color: manualStatusValue === 'PAID' ? '#079455' : '',
        }),
        menu: (base) => ({
            ...base,
            width: '12.5rem',
        }),
        option: (base) => ({
            ...base,
            fontWeight: 500,
            fontSize: '0.75rem',
            // text-base color
            color: '#242424',
        }),
    };

    if (isLoading || isLatestPostPaymentRequestFetching) {
        return <Skeleton />;
    }

    if (!isPaymentStatusEditable) {
        const primaryPaymentStatus = latestPostPaymentRequest
            ? getPrimaryPaymentStatus(latestPostPaymentRequest)
            : paymentRequestStatus;
        return (
            <Select
                value={{
                    value: primaryPaymentStatus,
                    label: getPaymentStatusLabel(primaryPaymentStatus),
                }}
                isSearchable={false}
                isClearable={false}
                styles={selectStyles}
                formatOptionLabel={formatOptionLabel}
                isDisabled={true}
                components={{
                    DropdownIndicator: () => null,
                }}
            />
        );
    }

    const selectedOption = OPTIONS.find(
        (option) => option.value === paymentRequestStatus || option.value === initialStatus
    ) || {
        // To account for unexpected cases, we'll fallback to rendering the initial status directly
        value: initialStatus,
        label: capitalize(initialStatus.toLowerCase()),
    };

    return (
        <>
            <Select<typeof selectedOption>
                options={SELECTABLE_OPTIONS}
                value={selectedOption}
                onChange={(opt) => {
                    if (opt?.value === 'PAID' || opt?.value === 'UNPAID') {
                        setManualStatusValue(opt.value);
                        handleUpdate(opt.value);
                    }
                }}
                isSearchable={false}
                isClearable={false}
                styles={selectStyles}
                formatOptionLabel={formatOptionLabel}
            />
        </>
    );
};

export default PaymentStatusCell;
